import React, { useMemo } from 'react';
import { Box, Typography, Paper, Chip, Tooltip } from '@mui/material';
import { MapContainer, TileLayer, Polygon, Marker, Popup, Circle } from 'react-leaflet';
import { LatLngExpression } from 'leaflet';
import { AccessTime, CheckCircle, Cancel, Phone } from '@mui/icons-material';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { ServiceProvider } from '../../services/serviceProviderService';

// Fix for default marker icon in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface ScheduleMapProps {
  providers: ServiceProvider[];
  selectedDay: string;
  defaultCenter?: LatLngExpression;
  defaultZoom?: number;
}

// Helper to normalize coordinates
const normalizeCoordinates = (coords: any): LatLngExpression[] => {
  if (!coords || !Array.isArray(coords) || coords.length === 0) return [];
  return coords.map((coord: any) => {
    if (Array.isArray(coord) && coord.length >= 2) {
      return [coord[0], coord[1]] as LatLngExpression;
    }
    return coord as LatLngExpression;
  });
};

// Get polygon center for marker placement
const getPolygonCenter = (coordinates: LatLngExpression[]): LatLngExpression => {
  if (coordinates.length === 0) return [23.5859, 58.4059]; // Default to Muscat, Oman
  const validCoords = coordinates.filter(c => Array.isArray(c) && c.length >= 2) as number[][];
  if (validCoords.length === 0) return [23.5859, 58.4059];
  
  const sum = validCoords.reduce(
    (acc, coord) => [acc[0] + coord[0], acc[1] + coord[1]],
    [0, 0]
  );
  return [sum[0] / validCoords.length, sum[1] / validCoords.length];
};

// Check if provider is available on selected day
const isProviderAvailable = (provider: ServiceProvider, day: string): boolean => {
  const attrs = provider.attributes || provider;
  if (!attrs.schedule || typeof attrs.schedule !== 'object') return false;
  return attrs.schedule[day]?.active || false;
};

// Get provider schedule for a day
const getProviderSchedule = (provider: ServiceProvider, day: string) => {
  const attrs = provider.attributes || provider;
  if (!attrs.schedule || typeof attrs.schedule !== 'object') return null;
  return attrs.schedule[day];
};

// Get provider areas
const getProviderAreas = (provider: ServiceProvider) => {
  const attrs = provider.attributes || provider;
  if (attrs.areaPolygons?.polygons) {
    return attrs.areaPolygons.polygons;
  }
  return [];
};

const ScheduleMap: React.FC<ScheduleMapProps> = ({
  providers,
  selectedDay,
  defaultCenter = [23.5859, 58.4059], // Muscat, Oman
  defaultZoom = 11,
}) => {
  // Filter providers based on selected day
  const filteredProviders = useMemo(() => {
    if (selectedDay === 'all') {
      return providers;
    }
    return providers.filter(p => isProviderAvailable(p, selectedDay));
  }, [providers, selectedDay]);

  // Get all unique area polygons from available providers
  const allAreas = useMemo(() => {
    const areaMap = new Map<string, { polygon: any; providers: ServiceProvider[] }>();
    
    filteredProviders.forEach(provider => {
      const areas = getProviderAreas(provider);
      const isAvailable = selectedDay === 'all' || isProviderAvailable(provider, selectedDay);
      
      if (isAvailable && areas.length > 0) {
        areas.forEach((area: any) => {
          const key = area.id || area.name;
          if (!areaMap.has(key)) {
            areaMap.set(key, { polygon: area, providers: [] });
          }
          areaMap.get(key)!.providers.push(provider);
        });
      }
    });
    
    return Array.from(areaMap.values());
  }, [filteredProviders, selectedDay]);

  // Calculate map bounds to fit all areas
  const mapBounds = useMemo(() => {
    const allCoords: LatLngExpression[] = [];
    
    allAreas.forEach(({ polygon }) => {
      const coords = normalizeCoordinates(polygon.coordinates);
      allCoords.push(...coords);
    });
    
    // Add provider locations if they have coordinates
    filteredProviders.forEach(provider => {
      const attrs = provider.attributes || provider;
      if (attrs.locationLatitude && attrs.locationLongitude) {
        allCoords.push([attrs.locationLatitude, attrs.locationLongitude] as LatLngExpression);
      }
    });
    
    if (allCoords.length === 0) return null;
    
    const lats = allCoords.map(c => (c as number[])[0]);
    const lngs = allCoords.map(c => (c as number[])[1]);
    
    return [
      [Math.min(...lats), Math.min(...lngs)],
      [Math.max(...lats), Math.max(...lngs)]
    ] as [[number, number], [number, number]];
  }, [allAreas, filteredProviders]);

  // Create custom icons for available/unavailable providers
  const createCustomIcon = (isAvailable: boolean, providerName: string) => {
    return L.divIcon({
      className: 'custom-marker',
      html: `
        <div style="
          background-color: ${isAvailable ? '#4caf50' : '#9e9e9e'};
          color: white;
          border-radius: 50%;
          width: 30px;
          height: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          font-size: 12px;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        " title="${providerName}">
          ${providerName.charAt(0).toUpperCase()}
        </div>
      `,
      iconSize: [30, 30],
      iconAnchor: [15, 15],
    });
  };

  return (
    <Box sx={{ height: '100%', width: '100%', position: 'relative' }}>
      <MapContainer
        center={defaultCenter}
        zoom={defaultZoom}
        style={{ height: '100%', width: '100%' }}
        bounds={mapBounds as any}
        boundsOptions={{ padding: [50, 50] }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {/* Render service areas */}
        {allAreas.map(({ polygon, providers: areaProviders }, index) => {
          const coords = normalizeCoordinates(polygon.coordinates);
          if (coords.length === 0) return null;
          
          const center = getPolygonCenter(coords);
          const isAvailable = selectedDay === 'all' || 
            areaProviders.some(p => isProviderAvailable(p, selectedDay));
          
          return (
            <React.Fragment key={polygon.id || `area-${index}`}>
              <Polygon
                positions={coords}
                pathOptions={{
                  color: isAvailable ? '#3388ff' : '#cccccc',
                  fillColor: isAvailable ? '#3388ff' : '#e0e0e0',
                  fillOpacity: isAvailable ? 0.3 : 0.1,
                  weight: isAvailable ? 2 : 1,
                  dashArray: isAvailable ? undefined : '5, 5',
                }}
              />
              <Marker position={center}>
                <Popup>
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {polygon.name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {areaProviders.length} service provider{areaProviders.length !== 1 ? 's' : ''}
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      {areaProviders.map((provider) => {
                        const attrs = provider.attributes || provider;
                        const available = selectedDay === 'all' || isProviderAvailable(provider, selectedDay);
                        const schedule = selectedDay !== 'all' ? getProviderSchedule(provider, selectedDay) : null;
                        
                        return (
                          <Box key={provider.id} sx={{ mt: 0.5, p: 0.5, bgcolor: available ? '#f1f8f4' : '#fafafa', borderRadius: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {available ? (
                                <CheckCircle fontSize="small" color="success" />
                              ) : (
                                <Cancel fontSize="small" color="disabled" />
                              )}
                              <Typography variant="body2" fontWeight="medium">
                                {attrs.name}
                              </Typography>
                            </Box>
                            {available && schedule && (
                              <Typography variant="caption" color="textSecondary" sx={{ ml: 3 }}>
                                {schedule.from || '08:00'} - {schedule.to || '17:00'}
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                </Popup>
              </Marker>
            </React.Fragment>
          );
        })}

        {/* Render provider markers (if they have location coordinates but no areas) */}
        {filteredProviders.map((provider) => {
          const attrs = provider.attributes || provider;
          const hasAreas = getProviderAreas(provider).length > 0;
          
          // Only show marker if provider has location but no areas defined
          if (hasAreas || !attrs.locationLatitude || !attrs.locationLongitude) {
            return null;
          }
          
          const available = selectedDay === 'all' || isProviderAvailable(provider, selectedDay);
          const schedule = selectedDay !== 'all' ? getProviderSchedule(provider, selectedDay) : null;
          
          return (
            <Marker
              key={provider.id}
              position={[attrs.locationLatitude, attrs.locationLongitude] as LatLngExpression}
              icon={createCustomIcon(available, attrs.name || 'Provider')}
            >
              <Popup>
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {attrs.name}
                  </Typography>
                  <Chip label={attrs.type} size="small" sx={{ mt: 0.5, mb: 0.5 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <Phone fontSize="small" />
                    <Typography variant="body2">{attrs.phone}</Typography>
                  </Box>
                  {available ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                      <CheckCircle fontSize="small" color="success" />
                      <Typography variant="body2" color="success.main">
                        Available
                      </Typography>
                      {schedule && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                          <AccessTime fontSize="small" />
                          <Typography variant="caption">
                            {schedule.from || '08:00'} - {schedule.to || '17:00'}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                      <Cancel fontSize="small" color="disabled" />
                      <Typography variant="body2" color="text.secondary">
                        Not Available
                      </Typography>
                    </Box>
                  )}
                  {attrs.area && (
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                      Area: {attrs.area}
                    </Typography>
                  )}
                </Box>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>

      {/* Legend */}
      <Paper
        sx={{
          position: 'absolute',
          bottom: 10,
          right: 10,
          p: 2,
          zIndex: 1000,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
        }}
      >
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          Legend
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 20,
                height: 20,
                bgcolor: '#3388ff',
                borderRadius: '50%',
                border: '2px solid white',
              }}
            />
            <Typography variant="caption">Available Areas</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 20,
                height: 20,
                bgcolor: '#9e9e9e',
                borderRadius: '50%',
                border: '2px solid white',
              }}
            />
            <Typography variant="caption">Unavailable Areas</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 20,
                height: 20,
                bgcolor: '#4caf50',
                borderRadius: '50%',
                border: '2px solid white',
              }}
            />
            <Typography variant="caption">Available Provider</Typography>
          </Box>
        </Box>
        {selectedDay !== 'all' && (
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
            Showing: {selectedDay}
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default ScheduleMap;
